import { useState } from 'react';
import { Tab } from '@headlessui/react';
import { updateUserAttribute, confirmUserAttribute, deleteUser } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import userService from '../services/unifiedUserService';
import ceuService from '../services/unifiedCEUService';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Alert from '../components/ui/Alert';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import CycleExtensionManager from '../components/user/CycleExtensionManager';
import { User, Heart, Shield, ExternalLink, Mail, Calendar, AlertTriangle } from 'lucide-react';
import clsx from 'clsx';

export default function Settings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedTab, setSelectedTab] = useState(0);
  const [newEmail, setNewEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [showVerification, setShowVerification] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [cycleExpiration, setCycleExpiration] = useState('');

  const tabs = [
    { name: 'Account', icon: User },
    { name: 'Privacy', icon: Shield },
  ];

  const handleEmailChange = async () => {
    setEmailLoading(true);
    setEmailError('');
    setEmailSuccess('');

    try {
      await updateUserAttribute({
        userAttribute: {
          attributeKey: 'email',
          value: newEmail
        }
      });
      setShowVerification(true);
      setEmailSuccess('Verification code sent to your new email address');
    } catch (error) {
      setEmailError(error.message || 'Failed to update email');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleVerifyEmail = async () => {
    setEmailLoading(true);
    setEmailError('');
    setEmailSuccess('');

    try {
      await confirmUserAttribute({
        userAttributeKey: 'email',
        confirmationCode: verificationCode
      });
      setEmailSuccess('Email updated successfully!');
      setShowVerification(false);
      setNewEmail('');
      setVerificationCode('');
    } catch (error) {
      setEmailError(error.message || 'Failed to verify email');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);

    try {
      // 1. Delete all user's CEUs from DynamoDB
      const ceusResult = await ceuService.getCEUs(user.userId);
      if (ceusResult.success && ceusResult.data) {
        for (const ceu of ceusResult.data) {
          await ceuService.deleteCEU(user.userId, ceu.ceuId || ceu.id);
        }
      }

      // 2. Delete user profile from DynamoDB
      await userService.deleteUser(user.userId);

      // 3. Delete user from Cognito
      await deleteUser();

      // 4. Redirect to home
      navigate('/');
    } catch (error) {
      alert('Failed to delete account: ' + error.message);
      setDeleteLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-1">Manage your account preferences and privacy</p>
      </div>

      <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
        <Tab.List className="flex space-x-1 rounded-xl bg-primary/10 p-1 mb-6">
          {tabs.map((tab) => (
            <Tab
              key={tab.name}
              className={({ selected }) =>
                clsx(
                  'w-full rounded-lg py-2.5 text-sm font-medium leading-5',
                  'ring-white ring-opacity-60 ring-offset-2 ring-offset-primary focus:outline-none focus:ring-2',
                  selected
                    ? 'bg-white text-primary shadow'
                    : 'text-gray-700 hover:bg-white/[0.12] hover:text-primary'
                )
              }
            >
              <div className="flex items-center justify-center gap-2">
                <tab.icon className="w-4 h-4" />
                {tab.name}
              </div>
            </Tab>
          ))}
        </Tab.List>

        <Tab.Panels>
          {/* Account Tab */}
          <Tab.Panel>
            <div className="space-y-6">
              {emailError && <Alert variant="error">{emailError}</Alert>}
              {emailSuccess && <Alert variant="success">{emailSuccess}</Alert>}

              {/* Account Information */}
              <Card>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Account Information</h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Current Email</label>
                    <p className="mt-1 text-gray-900">{user?.email || 'Not available'}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">User ID</label>
                    <p className="mt-1 text-xs text-gray-600 font-mono">{user?.userId}</p>
                  </div>
                </div>
              </Card>

              {/* Change Email */}
              <Card>
                <div className="flex items-center gap-2 mb-4">
                  <Mail className="w-5 h-5 text-primary" />
                  <h2 className="text-lg font-semibold text-gray-900">Change Email Address</h2>
                </div>
                
                {!showVerification ? (
                  <div className="space-y-4">
                    <Input
                      type="email"
                      label="New Email Address"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="Enter new email address"
                    />
                    <Button
                      variant="primary"
                      onClick={handleEmailChange}
                      disabled={!newEmail || emailLoading}
                    >
                      {emailLoading ? 'Sending...' : 'Send Verification Code'}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Alert variant="info">
                      A verification code has been sent to {newEmail}. Please enter it below.
                    </Alert>
                    <Input
                      type="text"
                      label="Verification Code"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      placeholder="Enter 6-digit code"
                    />
                    <div className="flex gap-2">
                      <Button
                        variant="primary"
                        onClick={handleVerifyEmail}
                        disabled={!verificationCode || emailLoading}
                      >
                        {emailLoading ? 'Verifying...' : 'Verify Email'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowVerification(false);
                          setVerificationCode('');
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </Card>

              {/* Help & Support */}
              <Card>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Help & Support</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <ExternalLink className="w-5 h-5 text-primary" />
                      <h3 className="font-semibold text-gray-900">Share Feedback</h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      Help us improve CertifiAble by sharing your thoughts and suggestions.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open('https://forms.gle/sq3HRE1igLuWTuxD9', '_blank')}
                    >
                      Give Feedback
                    </Button>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Heart className="w-5 h-5 text-secondary" />
                      <h3 className="font-semibold text-gray-900">Support Development</h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      Help keep CertifiAble running with a voluntary contribution.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open('https://buy.stripe.com/7sYbJ34Vq3z14AHdO9f3a01?locale=en', '_blank')}
                    >
                      Support Us
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Extension Management */}
              <Card>
                <CycleExtensionManager />
              </Card>

              {/* Account Deletion */}
              <Card className="border-error/20">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-error" />
                  <h2 className="text-lg font-semibold text-error">Danger Zone</h2>
                </div>
                <div className="space-y-3">
                  <p className="text-sm text-gray-700 font-semibold">
                    Permanently delete your account and all associated data.
                  </p>
                  <div className="bg-error/5 border border-error/20 rounded-lg p-3">
                    <p className="text-sm text-error font-semibold mb-2">⚠️ WARNING: This action CANNOT be undone!</p>
                    <p className="text-sm text-gray-700">
                      All your CEU records, certificates, profile data, and account access will be permanently deleted. There is no way to recover this information once deleted.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    className="text-error border-error hover:bg-error/5"
                    onClick={() => setShowDeleteDialog(true)}
                  >
                    Delete Account & All Data
                  </Button>
                </div>
              </Card>
            </div>
          </Tab.Panel>

          {/* Privacy Tab */}
          <Tab.Panel>
            <div className="space-y-6">
              <Card>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Privacy & Legal</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-5 h-5 text-primary" />
                      <h3 className="font-semibold text-gray-900">Privacy Notice</h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      Review our privacy practices and how we handle your information.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (window.showPrivacyNotice) {
                          window.showPrivacyNotice();
                        } else {
                          alert('Privacy Notice is loading...');
                        }
                      }}
                    >
                      View Privacy Notice
                    </Button>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-5 h-5 text-secondary" />
                      <h3 className="font-semibold text-gray-900">Data Management</h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      Your data is encrypted and secure. You have full control.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.location.href = '/my-ceus'}
                    >
                      Export Data
                    </Button>
                  </div>
                </div>
              </Card>

              <Card>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Data Security</h2>
                <div className="space-y-3 text-sm text-gray-600">
                  <p>✓ All data is encrypted at rest using AWS KMS</p>
                  <p>✓ Secure authentication via AWS Cognito</p>
                  <p>✓ HTTPS/TLS encryption for all data in transit</p>
                  <p>✓ Regular security audits and updates</p>
                </div>
              </Card>
            </div>
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>

      {/* Delete Account Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteAccount}
        title="⚠️ Delete Account Permanently?"
        confirmText={deleteLoading ? "Deleting..." : "Yes, Delete Everything"}
        cancelText="Cancel"
        variant="danger"
      >
        <div className="space-y-4">
          <div className="bg-error/10 border-2 border-error rounded-lg p-4">
            <p className="text-error font-bold text-center text-lg mb-2">
              THIS ACTION CANNOT BE UNDONE
            </p>
            <p className="text-sm text-gray-700 text-center">
              Once deleted, there is no way to recover your account or data.
            </p>
          </div>

          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-error flex-shrink-0 mt-1" />
            <div className="text-sm text-gray-700">
              <p className="font-semibold text-gray-900 mb-2">This will permanently delete:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Your user account and profile information</li>
                <li>All CEU records and uploaded certificates</li>
                <li>All progress tracking and cycle data</li>
                <li>Your authentication credentials and login access</li>
              </ul>
              <p className="mt-4 font-bold text-error">
                Are you absolutely sure you want to proceed?
              </p>
            </div>
          </div>
        </div>
      </ConfirmDialog>
    </div>
  );
}
